/**
 * « Une nouvelle version de MADA est disponible. »
 *
 * POURQUOI. Après un déploiement, un onglet resté ouvert garde l'ancienne
 * version : ses écrans ne connaissent pas les nouveautés, et une page pas
 * encore visitée peut ne plus se charger (ses fichiers ont changé de nom).
 * Le service worker se met bien à jour seul, mais il ne prévient personne.
 *
 * COMMENT. On relit `index.html` sur le serveur (sans cache) et on compare le
 * script principal qu'il désigne à celui que la page a chargé : différent =
 * nouvelle version. Vérifié toutes les 2 minutes quand l'onglet est visible,
 * au retour sur l'onglet et au retour du réseau. Aucune lecture en
 * développement (Vite n'y produit pas de fichiers versionnés).
 *
 * METTRE À JOUR. Recharger ne suffit pas toujours : la page rechargée serait
 * servie par l'ancien service worker si le nouveau n'a pas encore pris la
 * main. On lui demande donc de se mettre à jour et on attend (5 s au plus)
 * qu'il prenne le contrôle, puis on recharge.
 *
 * PAGE QUI NE SE CHARGE PLUS (`vite:preloadError`). On recharge tout de suite,
 * une seule fois : si le même échec revient dans les 30 s, on n'insiste pas
 * (on bouclerait) et on affiche le bandeau à la place.
 */
import { onlineManager } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

const CHECK_INTERVAL_MS = 2 * 60 * 1000;
const CONTROLLER_WAIT_MS = 5_000;
const RELOAD_GUARD_KEY = "mada-update-reload";
const RELOAD_GUARD_MS = 30_000;

let available = false;
const listeners = new Set<() => void>();

function setAvailable(value: boolean): void {
  if (available === value) return;
  available = value;
  for (const listener of listeners) listener();
}

export function isUpdateAvailable(): boolean {
  return available;
}

export function subscribeUpdate(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useIsUpdateAvailable(): boolean {
  return useSyncExternalStore(subscribeUpdate, isUpdateAvailable, () => false);
}

/** Script principal désigné par un `index.html` (celui de `/assets/`). */
export function entryScriptOf(doc: Document): string | null {
  const script = doc.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]');
  return script?.getAttribute("src") ?? null;
}

/** Compare la version servie à la version chargée ; renvoie `true` si elle a changé. */
export async function checkForUpdate(): Promise<boolean> {
  if (available) return true;
  const loaded = entryScriptOf(document);
  if (!loaded) return false; // développement, ou page sans script versionné
  try {
    const res = await fetch(`/index.html?version=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res.ok) return false;
    const served = entryScriptOf(new DOMParser().parseFromString(await res.text(), "text/html"));
    if (served && served !== loaded) {
      setAvailable(true);
      return true;
    }
  } catch {
    /* hors ligne ou serveur absent : on réessaiera au prochain tour */
  }
  return false;
}

function waitForNewController(): Promise<void> {
  return new Promise((resolve) => {
    const sw = navigator.serviceWorker;
    const done = () => {
      sw.removeEventListener("controllerchange", done);
      resolve();
    };
    sw.addEventListener("controllerchange", done);
    setTimeout(done, CONTROLLER_WAIT_MS);
  });
}

/** Installe la nouvelle version et recharge la page. */
export async function applyUpdate(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.update();
      if (reg.installing || reg.waiting) await waitForNewController();
    }
  } catch {
    /* pas de service worker, ou mise à jour refusée : un simple rechargement */
  }
  window.location.reload();
}

/** Recharge une fois ; renvoie `false` si un rechargement vient d'avoir lieu. */
export function reloadOnce(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
    if (now - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    return false; // sans mémoire de session, on ne prend pas le risque de boucler
  }
  void applyUpdate();
  return true;
}

let installed = false;

/** À appeler une fois, au démarrage (`main.tsx`). */
export function installUpdateWatcher(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Une page qui ne se charge plus : c'est presque toujours une nouvelle version.
  window.addEventListener("vite:preloadError", (event) => {
    if (reloadOnce()) event.preventDefault();
    else setAvailable(true);
  });

  if (!import.meta.env.PROD) return;

  const checkIfVisible = () => {
    if (document.visibilityState === "visible") void checkForUpdate();
  };
  setInterval(checkIfVisible, CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", checkIfVisible);
  onlineManager.subscribe((online) => {
    if (online) void checkForUpdate();
  });
}

/** Remise à zéro — réservée aux tests. */
export function resetUpdateState(): void {
  available = false;
  listeners.clear();
}
